import styled from "styled-components";

import { Button } from "core/ButtonGroup/styles";
import colors from "core/colors";
import breakpoints from "core/breakpoints";
import { medium as mediumFont, textSm } from "core/fontStyles";
import { rapidEasing } from "core/animation";
import { focusOutline } from "core/links";
import { convertPxToRem } from "frontend/components/helpers";

export const Container = styled.div`
    cursor: pointer;
    display: flex;
    padding: ${convertPxToRem({ px: 16 })};
    margin-bottom: ${convertPxToRem({ px: 16 })};
    border-radius: ${convertPxToRem({ px: 4 })};
    border-width: ${convertPxToRem({ px: 2 })};
    border-style: solid;
    border-color: ${colors.concrete};
    background-color: ${colors.snow};
    color: ${colors.midnight};
    transition: background-color ${rapidEasing}, border ${rapidEasing};

    &:hover,
    input[type="radio"]:checked + & {
        border-color: ${colors.midnight};
        background-color: ${colors.midnight};
        color: ${colors.mist};

        // Adjust colors of inner button-group if outer button is selected or hovered
        ${Button} {
            background-color: ${colors.midnight};
            color: ${colors.coin};
            border-color: ${colors.porpoise};

            // Styles for the currently selected button
            &:nth-child(${({ activeOption }) => activeOption + 1}) {
                color: ${colors.midnight};
                background-color: ${colors.mist};
                border-color: ${colors.mist};
            }

            // Hover state for non-selected buttons in group
            &:not(:nth-child(${({ activeOption }) => activeOption + 1})):hover {
                background-color: ${colors.pebble};
                border-color: ${colors.porpoise};
            }
        }
    }

    // Focus state of outer button
    input[type="radio"]:focus-visible + & {
        ${focusOutline}
    }

    // Focus state of inner buttons
    ${Button}:focus-visible {
        ${focusOutline}
        z-index: 1000; // lift outline so it's not partially hidden by the next button
    }
`;

// NOTE - we use absolute position to remove from document flow and opacity to
//        make the element invisible. But, still maintain keyboard accessibility
export const RadioButton = styled.input.attrs({
    type: "radio"
})`
    opacity: 0;
    position: absolute;
`;

export const CheckedIndicator = styled.div`
    flex: 0 0;
    padding-right: ${convertPxToRem({ px: 16 })};
    margin-top: ${convertPxToRem({ px: -2 })};
`;

export const Price = styled.div`
    flex: 0 0;
    ${mediumFont}
    font-size: ${convertPxToRem({ px: 18 })};
    text-align: right;
    position: relative;

    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
      font-size: ${convertPxToRem({ px: 16 })};
    }
`;

export const Subscription = styled.div`
    margin-top: ${convertPxToRem({ px: 3 })};
    ${textSm}
`;

export const IconContainer = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;

    svg path {
        fill: ${colors.storm};

        label:hover &,
        input[type="radio"]:checked + ${Container} & {
            fill: ${colors.platinum};
        }
    }

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }
`;

export const Content = styled.div`
    flex: 1 0;
`;

export const Title = styled.div`
    margin-bottom: ${convertPxToRem({ px: 8 })};
    ${mediumFont}
    font-size: ${convertPxToRem({ px: 18 })};

    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
      font-size: ${convertPxToRem({ px: 16 })};
    }
`;

export const Details = styled.div`
    margin-top: ${convertPxToRem({ px: 8 })};
    line-height: ${convertPxToRem({ px: 19.2 })};
    color: ${colors.storm};
    ${mediumFont}

    a {
        color: ${colors.fossil};
        text-decoration: none;
        border-bottom: 1px solid ${colors.fossil};
    }

    label:hover &,
    input[type="radio"]:checked + ${Container} & {
        color: ${colors.platinum};

        a {
            color: ${colors.coin};
            border-bottom-color: ${colors.coin};
            transition: color ${rapidEasing}, border-bottom-color ${rapidEasing};

            &:hover {
                color: ${colors.mist};
                border-bottom-color: ${colors.mist};
            }
        }
    }
`;

export const Detail = styled.div`
    margin-right: ${convertPxToRem({ px: 11 })};
`;

export const HideOnMobile = styled.span`
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }
`;

export const BadgeContainer = styled.div`
    margin-top: ${convertPxToRem({ px: 13 })};
    margin-bottom: ${convertPxToRem({ px: 8 })};

    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
        margin-top: ${convertPxToRem({ px: 11 })};
    }

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        display: none;
    }
`;

export const BadgeText = styled.div`
    display: inline-block;
    ${textSm}
    line-height: 1;
    color: ${colors.midnight};
    background-color: ${colors.maya};
    padding: ${convertPxToRem({ px: 3 })} ${convertPxToRem({ px: 8 })};
`;
